/**
 * useAvatarLiveEventBridge - Window event bridge for hosted live avatar flows
 *
 * Keeps the browser event listener wiring separate from the core live session
 * controller so native embeds can reuse the core logic without inheriting the
 * window-based shell glue.
 */

import { MutableRefObject, useEffect } from 'react';

import type { MakeCallOptions } from '../../channels/voice/voiceCallFeature';
import type { AvatarSessionContext } from '../../session';
import type { LiveBlendshapeFrame } from './useLivePromptPlayback';
import type { RequestLiveSessionFn } from './useLiveSessionRequest';

interface LiveBridgeAvatarRef {
  setIsListening: (value: boolean) => void;
  setIsThinking: (value: boolean) => void;
  playBlendshapes?: (frames: LiveBlendshapeFrame[]) => void;
}

interface AnimateCallBlendData {
  blendData?: Array<{ time: number; blendshapes: Record<string, number> }>;
}

export interface UseAvatarLiveEventBridgeOptions {
  avatarRef: MutableRefObject<LiveBridgeAvatarRef | null>;
  projectTokenCamlin?: string;
  verboseAvatarDebug: boolean;
  liveModeActive: boolean;
  liveSessionToken?: string;
  isVoiceInitialized: boolean;
  isVisualOnly?: boolean;
  noSip?: boolean;
  ensureLiveContext: (overrides?: Partial<AvatarSessionContext>) => AvatarSessionContext;
  requestLiveSession: RequestLiveSessionFn;
  initiateCall: (target: string, options?: MakeCallOptions) => boolean;
  hasLiveClient: () => boolean;
  setAnimateCallSpeak: (value: AnimateCallBlendData | boolean) => void;
}

function buildMergedMetadata(detail: Record<string, unknown>): Record<string, unknown> | undefined {
  const explicitMetadata = detail.metadata && typeof detail.metadata === 'object'
    ? detail.metadata as Record<string, unknown>
    : {};
  const embeddedMetadata = Object.fromEntries(
    Object.entries(detail).filter(([key]) => ![
      'projectToken',
      'diagramId',
      'sessionId',
      'handoffToken',
      'channel',
      'tenantId',
      'userId',
      'metadata'
    ].includes(key))
  );

  return Object.keys({
    ...explicitMetadata,
    ...embeddedMetadata
  }).length > 0
    ? {
        ...explicitMetadata,
        ...embeddedMetadata
      }
    : undefined;
}

export function useAvatarLiveEventBridge({
  avatarRef,
  projectTokenCamlin,
  verboseAvatarDebug,
  liveModeActive,
  liveSessionToken,
  isVoiceInitialized,
  isVisualOnly,
  noSip,
  ensureLiveContext,
  requestLiveSession,
  initiateCall,
  hasLiveClient,
  setAnimateCallSpeak
}: UseAvatarLiveEventBridgeOptions): void {
  useEffect(() => {
    const handleInitiateCall = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail ?? {};
      const target = typeof detail.target === 'string' && detail.target.trim()
        ? detail.target
        : projectTokenCamlin;
      if (verboseAvatarDebug) {
        console.log('📞 avatar:initiateCall event received, target:', target);
      }

      const wantsLiveSession = liveModeActive || detail.mode === 'live' || detail.liveMode === true;
      if (wantsLiveSession) {
        requestLiveSession({
          projectToken: typeof detail.projectToken === 'string' ? detail.projectToken : undefined,
          diagramId: typeof detail.diagramId === 'string' ? detail.diagramId : undefined,
          sessionId: typeof detail.sessionId === 'string' ? detail.sessionId : undefined,
          handoffToken: typeof detail.handoffToken === 'string' ? detail.handoffToken : undefined,
          userId: typeof detail.userId === 'string' ? detail.userId : undefined
        }, !(liveModeActive ? isVisualOnly : (noSip || isVisualOnly)));
        return;
      }

      if (!isVoiceInitialized) {
        if (verboseAvatarDebug) {
          console.log('⚠️ Voice features not initialized yet');
        }
        return;
      }

      if (target) {
        initiateCall(target);
      }
    };

    const handleListening = (event: Event) => {
      const detail = (event as CustomEvent<{ listening: boolean }>).detail;
      if (verboseAvatarDebug) {
        console.log('👂 avatar:listening event received:', detail);
      }
      avatarRef.current?.setIsListening(detail.listening);
    };

    const handleThinking = (event: Event) => {
      const detail = (event as CustomEvent<{ thinking: boolean }>).detail;
      if (verboseAvatarDebug) {
        console.log('🤔 avatar:thinking event received:', detail);
      }
      avatarRef.current?.setIsThinking(detail.thinking);
    };

    const handleBlendshapePlayback = (event: Event) => {
      const frames = (event as CustomEvent<{ frames: LiveBlendshapeFrame[] }>).detail?.frames ?? [];
      if (!frames.length) {
        return;
      }

      const blendData = frames.map((frame) => ({
        time: frame.time,
        blendshapes: frame.values
      }));

      if (verboseAvatarDebug) {
        console.log('🎭 avatar:blendshapes:play event received:', blendData.length);
      }
      if (typeof avatarRef.current?.playBlendshapes === 'function') {
        avatarRef.current.playBlendshapes(frames);
        return;
      }

      setAnimateCallSpeak({ blendData });
    };

    const handleSessionContext = (event: Event) => {
      const detail = ((event as CustomEvent<Record<string, unknown>>).detail ?? {}) as Record<string, unknown>;
      const incomingSessionId = typeof detail.sessionId === 'string' ? detail.sessionId : undefined;
      const incomingHandoffToken = typeof detail.handoffToken === 'string' ? detail.handoffToken : undefined;
      const nextContext = ensureLiveContext({
        projectToken: typeof detail.projectToken === 'string' ? detail.projectToken : undefined,
        diagramId: typeof detail.diagramId === 'string' ? detail.diagramId : undefined,
        sessionId: incomingSessionId,
        handoffToken: incomingHandoffToken,
        channel: typeof detail.channel === 'string' ? detail.channel : undefined,
        tenantId: typeof detail.tenantId === 'string' ? detail.tenantId : undefined,
        userId: typeof detail.userId === 'string' ? detail.userId : undefined,
        metadata: buildMergedMetadata(detail)
      });
      (window as any).__CAMLIN_AVATAR_SESSION_CONTEXT__ = nextContext;

      if (
        liveModeActive ||
        detail.channel === 'live' ||
        detail.mode === 'live' ||
        detail.liveMode === true
      ) {
        const matchesCurrentLiveSession = Boolean(
          (incomingHandoffToken && incomingHandoffToken === liveSessionToken) ||
          (incomingSessionId && incomingSessionId === liveSessionToken)
        );
        const hasExplicitSessionLink = Boolean(incomingHandoffToken || incomingSessionId);

        if (hasExplicitSessionLink && (!matchesCurrentLiveSession || !hasLiveClient())) {
          requestLiveSession({
            projectToken: typeof nextContext.projectToken === 'string' ? nextContext.projectToken : undefined,
            diagramId: typeof nextContext.diagramId === 'string' ? nextContext.diagramId : undefined,
            sessionId: incomingSessionId,
            handoffToken: incomingHandoffToken,
            userId: typeof nextContext.userId === 'string' ? nextContext.userId : undefined,
            metadata: nextContext.metadata
          }, false);
        }
      }
    };

    window.addEventListener('avatar:initiateCall', handleInitiateCall as EventListener);
    if (verboseAvatarDebug) {
      console.log('📞 avatar:initiateCall listener registered');
    }
    window.addEventListener('avatar:listening', handleListening as EventListener);
    window.addEventListener('avatar:thinking', handleThinking as EventListener);
    window.addEventListener('avatar:blendshapes:play', handleBlendshapePlayback as EventListener);
    window.addEventListener('avatar:session:context', handleSessionContext as EventListener);
    if (verboseAvatarDebug) {
      console.log('👂🤔 avatar:listening and avatar:thinking listeners registered');
    }

    return () => {
      window.removeEventListener('avatar:initiateCall', handleInitiateCall as EventListener);
      window.removeEventListener('avatar:listening', handleListening as EventListener);
      window.removeEventListener('avatar:thinking', handleThinking as EventListener);
      window.removeEventListener('avatar:blendshapes:play', handleBlendshapePlayback as EventListener);
      window.removeEventListener('avatar:session:context', handleSessionContext as EventListener);
    };
  }, [
    avatarRef,
    ensureLiveContext,
    hasLiveClient,
    initiateCall,
    isVoiceInitialized,
    isVisualOnly,
    liveModeActive,
    liveSessionToken,
    noSip,
    projectTokenCamlin,
    requestLiveSession,
    setAnimateCallSpeak,
    verboseAvatarDebug
  ]);
}

export default useAvatarLiveEventBridge;
